import { EmptyGuid, EmptyImageDataStream } from "./constants";
import { StringUtilities } from "./string";
import { ValidationHelper } from "./validation-helper";

export interface ISelect2Item {
    id: string;
    text: string;
    value?: string;
    selected: boolean;
}

export module FormService {

    // Ensures all the properties of the object start with a lowercase letter
    export function convertObjectKeysToCamelCase(obj: any): any {
        if (typeof obj !== 'object' || obj === null) {
            return obj; // If obj is not an object or is null, return it unchanged
        }

        const convertedObj: any = Array.isArray(obj) ? [] : {}; // Create a new object or array to store the converted keys

        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const newKey = key[0].toLowerCase() + key.slice(1); // Convert the first character of the key to lowercase
                convertedObj[newKey] = convertObjectKeysToCamelCase(obj[key]); // Recursively convert nested objects
            }
        }

        return convertedObj;
    }

    export function clearSelectedOptionsFromSelect2(controlSelector: string) {
        $(controlSelector).val(null).trigger('change');
    }

    export function clearOptionsFromSelect2(controlSelector: string) {
        $(controlSelector).empty().trigger('change');
        
        if ($(controlSelector).attr('multiple') == null) {
            // Add the empty option to single selects to stop a bug when searching where the searched text becomes the selected option
            FormService.addOptionToSelect2(controlSelector, { id: '', text: '', selected: false })
        }
    }

    export function addOptionsToSelect2(controlSelector: string, items: ISelect2Item[]) {
        var $select2 = $(controlSelector);
        var dataAdapter = $select2.data().select2.dataAdapter;

        // If not null, this means that the select2 uses ajax to fetch options
        if (dataAdapter.ajaxOptions != null) {
            // This allows us to add the option with extra fields (i.e. value)
            var $options = dataAdapter.convertToOptions(items);
            dataAdapter.addOptions($options);
        }
        else {
            var selectedItems = items.filter((item: ISelect2Item) => {
                return item.selected;
            });

            var options = items.map(item => {
                return new Option(item.text, item.id, item.selected, item.selected);
            });
            $select2.append(options).trigger('change');

            // manually trigger the `select2:select` event
            $select2.trigger({
                type: 'select2:select',
                params: {
                    data: selectedItems as any
                }
            });
        }
    }

    export function addOptionToSelect2(controlSelector: string, item: ISelect2Item) {
        var $select2 = $(controlSelector);
        var dataAdapter = $select2.data()?.select2?.dataAdapter;

        // If not null, this means that the select2 uses ajax to fetch options
        if (dataAdapter?.ajaxOptions != null) {
            // This allows us to add the option with extra fields (i.e. value)
            var $option = dataAdapter.convertToOptions([item]);

            // $option is only returned if an option with the same id doesn't already exist.
            if ($option.length == 1) {
                dataAdapter.addOptions($option);
            }
            else {
                $select2.val(item.id).trigger('change');
            }
        }
        else {
            var option = new Option(item.text, item.id, item.selected, item.selected);

            $select2.append(option).trigger('change');
            //manually trigger the `select2:select` event
            $select2.trigger({
                type: 'select2:select',
                params: {
                    data: item
                }
            });
        }
    }

    export function isSelect2(controlSelector: string): boolean {
        var $control = $(controlSelector);

        if ($control.data('select2-id') != null) {
            return true;
        }

        return false;
    }

    // Does a case-insensitive search for a property name on an object and returns the value of the property
    // Also searches dynamic fields
    export function getPropertyValueByName(obj: any, propertyName: string): string {
        var matchingKey = getMatchingPropertyName(obj, propertyName as string);

        if (matchingKey) {
            return obj[matchingKey] || obj.dynamicFields[matchingKey];
        }

        return undefined;
    }

    // Does a case-insensitive search for a property name on an object and returns the matched property name
    // Also searches dynamic fields
    export function getMatchingPropertyName(obj: any, searchName: string): string | undefined {
        const keys = Object.keys(obj);
        const dynamicKeys = Object.keys(obj.dynamicFields);

        // Check for an exact match
        if (keys.includes(searchName) || dynamicKeys.includes(searchName)) {
            return searchName;
        }
        
        // Perform a case-insensitive match
        const lowerCaseSearchName = searchName.toLowerCase();
        const matchingKey = keys.find((key) => key.toLowerCase() === lowerCaseSearchName) 
                          || dynamicKeys.find((key) => key.toLowerCase() === lowerCaseSearchName);

        return matchingKey;
    }

    // Determines if a value is a non-default key value.
    // e.g. if PK is an int, then a valid value is > 0.  If it's a guid then it's not EmptyGuid etc
    export function isKeySet(keyValue: any) : boolean {
        if (keyValue == EmptyGuid || keyValue == 0)
            return false;

        return true;
    }

    // Given a list of form element selectors, return a dictionary of property names to their values
    // If convertKeysToCamelCase is true, then the property names in the object will be converted to camel case
    export function getFormDataFromSelectors(selectors: string[], convertKeysToCamelCase: boolean = false): {[field: string] : object} {
        
        var result: {[field: string] : object} = {};

        for (var i = 0; i < selectors.length; i++) {
            var selector = selectors[i];

            $(selector).each(function (index: number, element: HTMLElement) {
                populateFormDataFromHtmlElement(element, result);
            })
        }

        if (convertKeysToCamelCase) {
            result = convertObjectKeysToCamelCase(result);
        }

        return result;
    }

    function populateFormDataFromHtmlElement(element: HTMLElement, data: any) {
        var $element = $(element);

        var id = $element.attr('id');
        var name = $element.attr('name');
        var type = $element.attr('type');
        var value = $element.val() as any;
        var forField = $element.data('for-field');
        var forProperty = $element.data('for-property');
        
        if (type == 'number') {
            // Empty values and invalid values return empty.
            // Empty values and number values are valid.
            var valueReturnedEmpty = value == '';
            var valueIsValid = ($element[0] as any).validity.valid

            if (valueReturnedEmpty && valueIsValid) {
                value = null;
            }
            else {
                // If you use val() this returns '' for numeric inputs if the typed text isn't valid'
                // valueAsNumber gives NaN if the value isn't valid
                value = ($element[0] as any).valueAsNumber;
            }
        }
        else if (type == 'checkbox') {
            value = $element.is(':checked');
        }
        else if ($element.is('select') && value == '') {
            value = null;
        }
        else if ($element.is('img')) {
            // Esignature?
            if ($element.hasClass('signature-image')) {
                if ($element.hasClass('blank')) {
                    return;
                } else {
                    value = $element.attr('src');
                }
            }
        }
        else if (type == 'datetime-local') {
            // Don't send null datetimes
            if (value == '')
                return;
            
            // Work out the local datetime offset at the datetime, so we can add it to our datetime string
            var offsetMinutes = new Date(value).getTimezoneOffset();
            var offsetHours = Math.abs(offsetMinutes) / 60;
            var offsetPrefix = offsetMinutes > 0 ? '-' : '+';

            var offsetString = offsetPrefix +
                (offsetHours < 10 ? '0' : '') + offsetHours + ':' +
                (offsetMinutes % 60 === 0 ? '00' : '30');

            value = value + offsetString;
        }
        else if (type == 'text') {
            // Don't send null strings
            if (value == '')
                return;
        }
        else if (type == null){
            // If the element is a select, it doesn't have a type set
            if ($element.is("select")) {
                if ($element.hasClass("select2")) {
                    // Select2 data is more accurately fetched through this call.
                    var selectedOptions: any[] = $element.select2('data');
                    var isMulti = ($element[0] as any).multiple;
                    var sendIdOnly = $element.hasClass("send-id-only");

                    if (selectedOptions.length == 1 && !isMulti) {
                        if (selectedOptions[0].value != "null") {
                            // We're assuming that any select's either save a dropdownoption or a linkedentityreference, which both include the id and label
                            value = sendIdOnly
                                ? selectedOptions[0].id
                                : { id: selectedOptions[0].id, label: selectedOptions[0].text, value: selectedOptions[0].value };
                        }
                        else {
                            return;
                        }
                    }
                    else if (selectedOptions.length >= 1 && isMulti) {
                        value = [...selectedOptions].map(function (option) {
                            return sendIdOnly
                                ? option.id
                                : { id: option.id, label: option.text, value: option.value }
                        });
                    }
                    else {
                        return;
                    }
                }
                else {
                    var selectedOptions: any[] = ($element[0] as any).selectedOptions;
                    var isMulti = ($element[0] as any).multiple;
                    var sendIdOnly = $element.hasClass("send-id-only");

                    if (selectedOptions.length == 1 && !isMulti) {
                        if (selectedOptions[0].value != "null") {
                            // We're assuming that any select's either save a dropdownoption or a linkedentityreference, which both include the id and label
                            value = sendIdOnly
                                ? selectedOptions[0].value
                                : { id: selectedOptions[0].value, label: selectedOptions[0].innerText };
                        }
                        else {
                            return;
                        }
                    }
                    else if (selectedOptions.length >= 1 && isMulti) {
                        value = [...selectedOptions].map(function (option) {
                            return sendIdOnly
                                ? option.value
                                : { id: option.value, label: selectedOptions[0].innerText }
                        });
                    }
                    else {
                        return;
                    }
                }
            }
        }

        // Don't add null values to the data object
        if (value != null) {
            if (forField != null) {
                if (forProperty != null) {
                    if (data[forField] == null) {
                        data[forField] = {};
                    }

                    data[forField][forProperty] = value;
                }
                else {
                    data[forField] = value;
                }
            }
            else if (name != null) {
                data[name] = value;
            }
            else {
                data[id] = value;
            }
        }        
    }

    export function populateHtmlElementFromData(elementSelector: string, data: any): void {
        var $element = $(elementSelector);

        var id = $element.attr('id');
        var name = $element.attr('name');
        var type = $element.attr('type');
        var forField = $element.data('for-field');
        var forProp = $element.data('for-property');

        var dataFieldName = forField != null
                            ? StringUtilities.toCamelCase(forField)
                            : getMatchingPropertyName(data, name || id);
        var dataFieldProperty = forProp != null
                                ? StringUtilities.toCamelCase(forProp)
                                : null;
        var dataFieldNameValue = null;
        var dataFieldValue = null;

        // If we're mapping from a dynamic field, then we need to get the data from the DynamicFields property
        if ($element.hasClass('trifort-cms-dynamic-field')) {
            if (data['dynamicFields'] != null) {
                dataFieldNameValue = data['dynamicFields'][dataFieldName];
            }
            // If we don't have any value, apply a default.  This might be if a new field was added to the entity model, but not yet to the entity
            if (dataFieldNameValue == undefined) {
                dataFieldNameValue = getDefaultfieldValue(type);
            }
        } else {
            dataFieldNameValue = data[dataFieldName];
        }

        // If this element is only showing a property of a field, get that next.
        if (dataFieldProperty != null) {
            dataFieldValue = dataFieldNameValue != null ? dataFieldNameValue[dataFieldProperty] : null;
        }
        else {
            dataFieldValue = dataFieldNameValue;
        }

        if (type == 'checkbox') {
            $element.prop('checked', dataFieldValue);
        }
        else if (type == 'date') {
            // Date fields might come back in ISO8601 format, e.g. 2021-01-01T00:00:00, but the date control expects YYYY-MM-DD
            if (dataFieldValue == null) dataFieldValue = '';

            dataFieldValue = dataFieldValue.split('T')[0];
            $element.val(dataFieldValue);
        }
        else if (type == 'datetime-local') {
            // Stored as a DateTimeOffset, e.g. 2021-01-01T00:00:00+00:00, but the date control expects YYYY-MM-DDTHH:mm:ss
            if (dataFieldValue == null || dataFieldValue == '') {
                $element.val(null);
            } else {
                var date = new Date(dataFieldValue);
                $element.val(formatDateTimeToISO(date));
            }
        }
        else if ($element.is('img')) {
            // Esignature?
            if ($element.hasClass('signature-image')) {
                if (dataFieldValue == null || dataFieldValue == EmptyImageDataStream) {
                    $element.attr('src', EmptyImageDataStream);
                    $element.addClass('blank');
                } else {
                    $element.attr('src', dataFieldValue);
                    $element.removeClass('blank');
                }
            }
        }
        else if ($element.is('select')) {
            var isSelect2 = $element.data('select2-id') != null;
            if (isSelect2) {
                // Unselect any existing selected options
                $element.val(null).trigger('change');

                // Select the new option(s)
                if (dataFieldValue != null) {
                    var isMultiple = $element.attr('multiple') != null;
                    if (isMultiple) {
                        var selectedIds = dataFieldValue.map((x: ISelect2Item) => x.id);

                        $element.val(selectedIds).trigger('change');
                    }
                    else {
                        var existingOption = $element.find("option[value='" + dataFieldValue.id + "']");
                        if (existingOption.length > 0) {
                            existingOption.prop('selected', true).trigger('change');
                        }
                        else {
                            var newOption = new Option(dataFieldValue.label, dataFieldValue.id, true, true);
                            $element.append(newOption).trigger('change');
                        }
                    }
                }
            } else {
                if (dataFieldValue != null && dataFieldValue.id != null) {
                    // if dataFieldValue is a dropdownOption, just get the id
                    dataFieldValue = dataFieldValue.id;
                }

                var existingOption = $element.find("option[value='" + dataFieldValue + "']");
                if (existingOption.length > 0) {
                    existingOption.prop('selected', true).trigger('change');
                }
            }
        }
        else if ($element.prop('type') == 'file') {
            // Handle File Uploads separately
            return;
        }
        else {
            $element.val(dataFieldValue);
        }
    }

    function getDefaultfieldValue(fieldType: string): any {
        if (fieldType == null) {
            return null;
        }

        switch (fieldType) {
            case 'bool':
                return false;
            default:
                return null;
        }
    }

    export function clearForm(formSelector: string) {
        var inputs = $(formSelector + ' input');
        inputs.val('');

        // Signature panels
        var signaturePanels = $(formSelector + ' .signature-image.trifort-cms-input');
        signaturePanels.attr('src', EmptyImageDataStream);

        // Select lists
        var selectLists = $(formSelector + ' select');
        selectLists.val(null);
        selectLists.trigger('change');

        // Clear form errors
        ValidationHelper.RemoveFormErrors(formSelector)
    }

    // Given a form element selector, return a dictionary of property names to their values
    export function getFormData(selectors: string[]): object {
        var result: any = {
        };

        for (var i = 0; i < selectors.length; i++) {
            var selector = selectors[i];

            var $control = $(selector);

            if ($control.length != 1) {
                console.error(selector + ' did not reference 1 control');
                break;
            }

            var controlIdentifier = $control.attr('name') || $control.attr('id');

            if (controlIdentifier == null || controlIdentifier.length == 0) {
                console.error(selector + 'does not have a name');
                break;
            }

            var type = $control.attr('type') || ($control[0] as any).type;

            if (type == 'select-multiple') {
                result[controlIdentifier] = this.getMultiSelectSelectedValues(selector);
            }
            else if (type == 'datetime-local' && value == '') {
                // Dont' send null datetimes
            }
            else if (type == 'img' && $control.hasClass('signature-image')) {
                // Esignature?
                if ($control.hasClass('blank')) {
                    result[controlIdentifier] = EmptyImageDataStream;
                }
                else {
                    result[controlIdentifier] = $control.attr('src');
                }
            }
            else {
                var value = $control.val();

                if (type == 'checkbox') {
                    value = $(selector).is(':checked') ? 'true' : 'false';
                }

                result[controlIdentifier] = value;
            }
        }

        return result;
    }

    export function showModal(modalSelector: string) {
        $(modalSelector).modal('show');
    }

    export function hideModal(modalSelector: string) {
        $(modalSelector).modal('hide');
    }

    export function formatDateTimeToISO(dateTime: Date): string {
        const year = dateTime.getFullYear();
        const month = (dateTime.getMonth() + 1).toString().padStart(2, '0');
        const day = dateTime.getDate().toString().padStart(2, '0');
        const hours = dateTime.getHours().toString().padStart(2, '0');
        const minutes = dateTime.getMinutes().toString().padStart(2, '0');
        
        const formattedDateString = `${year}-${month}-${day}T${hours}:${minutes}`;
        return formattedDateString;
    }
}